import React, { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetCrossClientSelection,
  selectCrossClientPageResults,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedResultIds,
  setCrossClientPageSelection,
} from '../../../store/crossClient/crossClientResults'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery } from '../../../store/crossClient/crossClient'
import { CrossClientChooseActionDropdown } from './CrossClientChooseActionDropdown'
import { pluralize } from '../utils'
import './CrossClientSelectionBanner.scss'

export const CrossClientSelectionBanner: React.FC = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const pageResults = useAppSelector(selectCrossClientPageResults)
  const selectedIds = useAppSelector(selectCrossClientSelectedResultIds)

  const [triggerGetAllResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const checkboxRef = useRef<HTMLInputElement>(null)

  const pageIds = pageResults.map((result) => result.id)
  const selectedSet = new Set(selectedIds)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id))

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = !allPageSelected
    }
  }, [allPageSelected])

  const handleToggleHeader = () => {
    if (selectedIds.length) {
      dispatch(resetCrossClientSelection())
    } else {
      dispatch(setCrossClientPageSelection({ ids: pageIds, selected: true }))
    }
  }

  const resolveTargets = useCallback(async () => {
    if (!execution?.id) {
      return []
    }
    const response = await triggerGetAllResults({ accountId, executionId: execution.id, resultIds: selectedIds }).unwrap()
    return response.data
  }, [accountId, execution, selectedIds, triggerGetAllResults])

  return (
    <div className='cross-client-selection-banner d-flex align-items-center gap-3 px-3 py-2'>
      <input
        ref={checkboxRef}
        type='checkbox'
        className='form-check-input m-0'
        aria-label='Clear selection'
        checked={selectedIds.length > 0}
        onChange={handleToggleHeader}
      />
      <span className='paragraph-small fw-bold zocks-text-gray-900'>
        {selectedIds.length.toLocaleString()} {pluralize(selectedIds.length, 'client')} selected
      </span>
      <CrossClientChooseActionDropdown
        count={selectedIds.length}
        scope='manual bulk'
        resolveTargets={resolveTargets}
        toggleVariant='borderless'
        toggleClassName='button-small fw-bold zocks-text-primary p-0 bg-transparent border-0'
      />
    </div>
  )
}
